import type { CoachServiceStopRequest, RequestCoachServiceStopRequest } from "@billiard/contracts";
import { isCoachServiceStopRequest } from "@billiard/contracts";

export interface CoachServiceStopRequestContext {
  tenantId: string;
  storeId: string;
  actorId: string;
}

export type CoachServiceStopFixedCommand =
  | {
      kind: "REQUEST_STOP";
      serviceId: string;
      request: RequestCoachServiceStopRequest;
      expected: {
        coachId: string;
        tableNumber: string;
        businessDate: string;
      };
    }
  | {
      kind: "COMPLETE_STOP";
      serviceId: string;
      action: "COMPLETE";
      reason: string | null;
      stopRequest: CoachServiceStopRequest;
      idempotencyKey: string;
    };

export type CoachServiceStopRequestRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface CoachServiceStopRequestRecovery extends CoachServiceStopRequestContext {
  command: CoachServiceStopFixedCommand;
  state: CoachServiceStopRequestRecoveryState;
}

export type CoachServiceStopRequestRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CoachServiceStopRequestRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH"; canRetry: false }
  | { status: "COMMAND_MISMATCH"; canRetry: false }
  | { status: "STORAGE_UNAVAILABLE"; canRetry: false; pendingOutcomeMayExist: boolean };

interface RequestFailureShape {
  code?: unknown;
  statusCode?: unknown;
}

export const COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY =
  "billiard-erp:coach-service-stop-request-recovery:v1";

let pendingRecovery: CoachServiceStopRequestRecovery | null = null;
let dispatchingRecovery: CoachServiceStopRequestRecovery | null = null;
let hydrated = false;
let storageUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value.trim() === value &&
    [...value].length >= minimum &&
    [...value].length <= maximum
  );
}

function contextIsValid(value: unknown): value is CoachServiceStopRequestContext {
  return (
    isRecord(value) &&
    isBoundedString(value.tenantId, 1, 256) &&
    isBoundedString(value.storeId, 1, 256) &&
    isBoundedString(value.actorId, 1, 256)
  );
}

function isCanonicalBusinessDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function requestIsValid(value: unknown): value is RequestCoachServiceStopRequest {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["expectedServiceVersion", "reason", "idempotencyKey"])
  ) {
    return false;
  }
  return (
    Number.isSafeInteger(value.expectedServiceVersion) &&
    (value.expectedServiceVersion as number) >= 1 &&
    isBoundedString(value.reason, 2, 500) &&
    isBoundedString(value.idempotencyKey, 8, 128)
  );
}

function commandIsValid(value: unknown): value is CoachServiceStopFixedCommand {
  if (!isRecord(value) || !isBoundedString(value.serviceId, 1, 64)) return false;
  if (value.kind === "REQUEST_STOP") {
    return (
      hasExactKeys(value, ["kind", "serviceId", "request", "expected"]) &&
      requestIsValid(value.request) &&
      isRecord(value.expected) &&
      hasExactKeys(value.expected, ["coachId", "tableNumber", "businessDate"]) &&
      isBoundedString(value.expected.coachId, 1, 64) &&
      isBoundedString(value.expected.tableNumber, 1, 32) &&
      isCanonicalBusinessDate(value.expected.businessDate)
    );
  }
  if (value.kind !== "COMPLETE_STOP") return false;
  return (
    hasExactKeys(value, [
      "kind",
      "serviceId",
      "action",
      "reason",
      "stopRequest",
      "idempotencyKey",
    ]) &&
    value.action === "COMPLETE" &&
    (value.reason === null || isBoundedString(value.reason, 2, 500)) &&
    isCoachServiceStopRequest(value.stopRequest) &&
    value.stopRequest.status === "PENDING" &&
    value.stopRequest.serviceId === value.serviceId &&
    isBoundedString(value.stopRequest.requestId, 1, 64) &&
    isBoundedString(value.idempotencyKey, 8, 128)
  );
}

function freezeDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return Object.freeze(value.map((item) => freezeDeep(item))) as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.freeze(
      Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [
          key,
          freezeDeep(item),
        ]),
      ),
    ) as T;
  }
  return value;
}

function immutableRecovery(
  context: CoachServiceStopRequestContext,
  command: CoachServiceStopFixedCommand,
  state: CoachServiceStopRequestRecoveryState,
): CoachServiceStopRequestRecovery {
  return freezeDeep(structuredClone({ ...context, command, state }));
}

function recoveryEquals(
  left: CoachServiceStopRequestRecovery,
  right: CoachServiceStopRequestRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function storedRecovery(value: unknown): CoachServiceStopRequestRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["tenantId", "storeId", "actorId", "command", "state"]) ||
    !contextIsValid(value.recovery) ||
    !commandIsValid(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery,
    value.recovery.command,
    value.recovery.state as CoachServiceStopRequestRecoveryState,
  );
}

function storageAccess(): { browser: boolean; storage: Storage | null } {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStored(storage: Storage): CoachServiceStopRequestRecovery | null {
  const encoded = storage.getItem(COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CoachServiceStopRequestRecovery): boolean {
  const access = storageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const saved = readStored(access.storage);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  const access = storageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    storageUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      storageUnavailable = true;
      return;
    }
    pendingRecovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(pendingRecovery)) storageUnavailable = true;
  } catch {
    storageUnavailable = true;
  }
}

function contextMatches(
  recovery: CoachServiceStopRequestRecovery,
  context: CoachServiceStopRequestContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.actorId === context.actorId
  );
}

function unavailableView(): CoachServiceStopRequestRecoveryView {
  return { status: "STORAGE_UNAVAILABLE", canRetry: false, pendingOutcomeMayExist };
}

export function coachServiceStopRequestContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): CoachServiceStopRequestContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, actorId: operator.id };
}

export function readCoachServiceStopRequestRecovery(
  context: CoachServiceStopRequestContext,
): CoachServiceStopRequestRecoveryView {
  hydrate();
  if (storageUnavailable || !contextIsValid(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "OUTCOME_UNKNOWN",
  };
}

export function claimCoachServiceStopRequestRecovery(
  context: CoachServiceStopRequestContext,
  command: CoachServiceStopFixedCommand,
): CoachServiceStopRequestRecoveryView {
  const current = readCoachServiceStopRequestRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!contextIsValid(context) || !commandIsValid(command)) {
    storageUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    storageUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(
  recovery: CoachServiceStopRequestRecovery,
  context: CoachServiceStopRequestContext,
): boolean {
  hydrate();
  if (
    storageUnavailable ||
    pendingRecovery !== recovery ||
    !contextIsValid(context) ||
    !contextMatches(recovery, context)
  ) {
    return false;
  }
  const access = storageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStored(access.storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      storageUnavailable = true;
      return false;
    }
    return true;
  } catch {
    storageUnavailable = true;
    return false;
  }
}

export function beginCoachServiceStopRequestDispatch(
  recovery: CoachServiceStopRequestRecovery,
  context: CoachServiceStopRequestContext,
): boolean {
  if (dispatchingRecovery || !verify(recovery, context)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishCoachServiceStopRequestDispatch(
  recovery: CoachServiceStopRequestRecovery,
  context: CoachServiceStopRequestContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCoachServiceStopRequestOutcomeUnknown(
  recovery: CoachServiceStopRequestRecovery,
  context: CoachServiceStopRequestContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const unknown = immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    storageUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearCoachServiceStopRequestRecovery(
  recovery: CoachServiceStopRequestRecovery,
  context: CoachServiceStopRequestContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const access = storageAccess();
  if (access.browser) {
    if (!access.storage) return false;
    try {
      access.storage.removeItem(COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY) !== null) {
        return false;
      }
    } catch {
      storageUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function coachServiceStopRequestOutcomeIsUnknown(error: unknown): boolean {
  if (!isRecord(error)) return true;
  const failure = error as RequestFailureShape;
  const code = typeof failure.code === "string" ? failure.code : undefined;
  const statusCode = typeof failure.statusCode === "number" ? failure.statusCode : undefined;
  if (
    code === "COACH_OPERATION_RESULT_UNKNOWN" ||
    code === "GATEWAY_UNREACHABLE" ||
    code === "GATEWAY_RESPONSE_INVALID" ||
    code === "OPERATOR_IDENTITY_CHANGED"
  ) {
    return true;
  }
  if (statusCode === 408) return true;
  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) return false;
  if (statusCode !== undefined && statusCode >= 500) return true;
  if (
    code === "REQUEST_VALIDATION_FAILED" ||
    code === "AUTH_REQUIRED" ||
    code === "NATIVE_SECURE_SESSION_UNAVAILABLE" ||
    code === "NATIVE_UPDATE_REQUIRED" ||
    code?.startsWith("AUTHORIZATION_")
  ) {
    return false;
  }
  return true;
}

export function resetCoachServiceStopRequestRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess().storage?.removeItem(COACH_SERVICE_STOP_REQUEST_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts from a fresh fail-closed state.
  }
}

export function resetCoachServiceStopRequestRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
}
